import { useState, useEffect, useMemo } from "react";

// React Query
import { useQueryClient } from "@tanstack/react-query";

// MUI
import { Stack, Typography } from "@mui/material";

// Helpers
import { getFormatDate } from "@Common/helpers/Dates";

// Constants
import { DATE_FORMAT } from "@/Constants/date";
import { EMPTY_RESULT, INITIAL_PAGE_PARAM, PAGINATION_SIZE } from "@/DataPages/constants/pagination";

// Types
import { IPageParam } from "@/Common/api/types/pagination";
import { ICustomerRegistrationActivity } from "@/DataPages/components/Inventory/types/dealerTrades";

// React Query
import { getConsumerRegistrationStatus } from "@/DataPages/api/inventory/queries";
import { requestConsumerRegistrationStatusIngestion } from "@/DataPages/api/inventory/mutations";
import { onSendConsumerRegistrationUpdated } from "@/DataPages/api/inventory/subscriptions";
import { useMutation } from "@tanstack/react-query";

// Hooks
import { usePaginatedResource } from "@/Common/hooks/Pagination";
import { buildGetNextPageParam } from "@/Common/hooks/Pagination/helpers";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import { useSubscriptions } from "@Common/context/subscriptions/Subscriptions";

// Components
import CountDownButton from "@Layouts/components/Buttons/CountDownButton";
import ConsumerRegistrationStatusTable from "@DataPages/components/Inventory/Tables/ConsumerRegistrationStatusTable";

type ConsumerRegistrationStatusManagementProps = {
	locationId: string;
	commissionCycleId: string;
	payPeriodId: string;
	updateNowBtnDisabled: boolean;
	tableTitle: string;
};

export default function ConsumerRegistrationStatusManagement(props: Readonly<ConsumerRegistrationStatusManagementProps>) {
	const { locationId, commissionCycleId, payPeriodId, updateNowBtnDisabled } = props;
	const [dateImported, setDateImported] = useState<Date | null>(null);
	const apiRef = useGridApiRef();
	const queryClient = useQueryClient();

	const {
		data: consumerRegistrationStatuses,
		isLoading,
		isFetching,
		hasNextPage,
	} = usePaginatedResource({
		queryKey: ["getConsumerRegistrationStatus", locationId, commissionCycleId, payPeriodId],
		queryFn: async (params) => {
			const pageParam = params.pageParam as IPageParam<string> | undefined;
			const pagination = pageParam && typeof pageParam === "object" ? pageParam : INITIAL_PAGE_PARAM;

			if (!("after" in pagination) || !("first" in pagination)) {
				throw new Error("PageParam should have properties 'after' and 'first'");
			}
			const paginatedResponse = await getConsumerRegistrationStatus(locationId, commissionCycleId, payPeriodId, pagination);
			if (!paginatedResponse) {
				return EMPTY_RESULT;
			}
			return paginatedResponse;
		},
		initialPageParam: INITIAL_PAGE_PARAM,
		getNextPageParam: buildGetNextPageParam(PAGINATION_SIZE),
		enabled: Boolean(locationId) && Boolean(commissionCycleId) && Boolean(payPeriodId),
		fetchAllPages: true,
		staleTime: 60000,
		gcTime: 60000,
	});

	const { mutate: ingestData } = useMutation({
		onMutate: () => {},
		mutationFn: async () => {
			if (!locationId) {
				return false;
			}
			return await requestConsumerRegistrationStatusIngestion(locationId);
		},
	});

	// Subscriptions
	const { addSubscription, getSubscription } = useSubscriptions();

	const subscription = getSubscription(["onSendConsumerRegistrationUpdated", locationId]);

	subscription?.subscriptions?.onSendConsumerRegistrationUpdated?.setCallback(() => {
		queryClient.invalidateQueries({
			queryKey: ["getConsumerRegistrationStatus"],
		});
	});

	useEffect(() => {
		if (locationId) {
			addSubscription(
				["onSendConsumerRegistrationUpdated", locationId],
				[
					{
						context: "onSendConsumerRegistrationUpdated",
						query: onSendConsumerRegistrationUpdated,
						variables: {
							locationId,
						},
					},
				]
			);
		}
	}, [locationId, addSubscription]);

	useEffect(() => {
		if (!consumerRegistrationStatuses.length || hasNextPage) {
			setDateImported(null);
			return;
		}

		let latest: Date | null = null;
		for (const node of consumerRegistrationStatuses) {
			const records = [node?.parent, ...(node?.children ?? [])];
			for (const record of records) {
				const d = record?.updatedAt ? new Date(record.updatedAt) : null;
				if (d && (!latest || d > latest)) latest = d;
			}
		}
		setDateImported(latest);
	}, [consumerRegistrationStatuses, hasNextPage]);

	// Helpers
	const makeParentKey = (r: any) =>
		[r.locationId, r.commissionCycleId, r.payPeriodId, r.stockNumber, r.dealNumber, r.businessPart].filter(Boolean).join("|");

	const rows = useMemo(() => {
		const out: ICustomerRegistrationActivity[] = [];
		for (const g of consumerRegistrationStatuses) {
			if (!g?.parent) continue;

			const parentKey = makeParentKey(g.parent);

			// Parent row (chevron row)
			out.push({
				...g.parent,
				_id: g.parent._id ? `parent:${g.parent._id}` : `parent:${parentKey}`,
				hierarchy: [parentKey],
			});

			// Children rows
			for (const c of g.children ?? []) {
				const childKey =
					c._id ?? c.globalId ?? [c.invitationStatus, c.invitationDateCreated, c.vehicleVin].filter(Boolean).join("~");

				out.push({
					...c,
					_id: `child:${childKey}`,
					hierarchy: [parentKey, childKey],
				});
			}
		}
		return out;
	}, [consumerRegistrationStatuses]);

	return (
		<>
			<ConsumerRegistrationStatusTable
				apiRef={apiRef}
				tableTitle={`${props.tableTitle} Consumer Registration Status`}
				isLoading={isLoading || isFetching}
				hasNextPage={hasNextPage}
				hierarchy="hierarchy"
				rows={rows}
			/>

			<Stack direction={"row"} className={"time-rate-bottomGrid"} alignItems={"center"}>
				<CountDownButton
					typographySx={{ fontSize: "12px" }}
					initialCopy={"Update Now"}
					actionCopy={"Updating..."}
					loading={isLoading}
					counter={60}
					onClickCallBack={() => ingestData()}
					disabled={updateNowBtnDisabled}
				/>
				<Typography className={"!font-sans !text-[14.5px] !text-[#666666] ps-2"}>
					{dateImported && "Imported date " + getFormatDate(dateImported, DATE_FORMAT.usDateTime12hPadded)}
				</Typography>
			</Stack>
		</>
	);
}
